import { useCallback, useEffect, useRef, useState } from 'react';
import type { Timeframe } from '../types';
import { replayAuthService, type PendingReplayContext } from '../services/replayAuthService';
import { useClerkAuth } from './useClerkAuth';

export interface ReplayGateController {
  ensureSignedInForReplay: (context?: PendingReplayContext) => boolean;
  isAuthLoaded: boolean;
  isSignedIn: boolean;
  isPendingReplayRedirect: boolean;
}

export interface UseReplayAuthGateParams {
  replayMode: 'off' | 'selecting' | 'active';
  enterReplaySelectingMode: () => void;
  restoreReplayContext?: (context: PendingReplayContext) => void;
  autoFetchSelectingMode?: (context?: PendingReplayContext) => void | Promise<void>;
  candlesLength?: number;
  isFetchingData?: boolean;
  isInitialLoading?: boolean;
  isRestoringSession?: boolean;
}

export function useReplayAuthGate(params: UseReplayAuthGateParams): ReplayGateController {
  const {
    replayMode,
    enterReplaySelectingMode,
    restoreReplayContext,
    autoFetchSelectingMode,
    candlesLength,
    isFetchingData,
    isInitialLoading,
    isRestoringSession,
  } = params;
  const { isAuthLoaded, isSignedIn, openSignIn } = useClerkAuth();

  const [isPendingReplayRedirect, setIsPendingReplayRedirect] = useState<boolean>(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('replay') === '1';
    } catch {
      return false;
    }
  });

  const didAutoFetchSelectingRef = useRef(false);

  const ensureSignedInForReplay = useCallback((context?: PendingReplayContext): boolean => {
    if (!isAuthLoaded) {
      console.warn('[useReplayAuthGate] Auth not loaded yet; blocking replay action');
    }

    const returnUrl = (() => {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('replay', '1');
        if (context?.ticker) url.searchParams.set('ticker', context.ticker);
        if (context?.timeframe) url.searchParams.set('tf', context.timeframe);
        return url.toString();
      } catch {
        return undefined;
      }
    })();

    return replayAuthService.ensureSignedInForReplay(
      { isAuthLoaded, isSignedIn: !!isSignedIn },
      { openSignIn },
      context,
      { returnUrl }
    );
  }, [isAuthLoaded, isSignedIn, openSignIn]);

  const isValidTimeframe = useCallback((value: string | null): value is Timeframe => {
    if (!value) return false;
    return (
      value === '1m' ||
      value === '5m' ||
      value === '15m' ||
      value === '30m' ||
      value === '1h' ||
      value === '4h' ||
      value === '1d' ||
      value === '1w'
    );
  }, []);

  useEffect(() => {
    if (!isPendingReplayRedirect) return;

    if (replayMode !== 'off') {
      setIsPendingReplayRedirect(false);
      return;
    }

    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('replay') !== '1') {
        setIsPendingReplayRedirect(false);
      }
    } catch {
      setIsPendingReplayRedirect(false);
    }
  }, [isPendingReplayRedirect, replayMode]);

  useEffect(() => {
    if (!isAuthLoaded || !isSignedIn) return;
    if (replayMode !== 'off') return;

    let shouldEnterReplay = replayAuthService.shouldEnterReplayAfterAuth();
    let ctxFromUrl: PendingReplayContext | null = null;

    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get('replay') === '1';
      if (fromUrl) {
        shouldEnterReplay = true;

        const ticker = params.get('ticker') || undefined;
        const tfRaw = params.get('tf');
        const timeframe = isValidTimeframe(tfRaw) ? tfRaw : undefined;
        ctxFromUrl = { ticker, timeframe };

        params.delete('replay');
        params.delete('ticker');
        params.delete('tf');
        const query = params.toString();
        const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
        window.history.replaceState({}, '', nextUrl);
      }
    } catch {
      // ignore
    }

    if (!shouldEnterReplay) return;

    const ctxFromStorage = replayAuthService.getPendingReplayContext();
    const ctx = ctxFromUrl || ctxFromStorage || undefined;
    if (ctx && restoreReplayContext) {
      restoreReplayContext(ctx);
    }

    replayAuthService.clearPendingReplayEnter();
    enterReplaySelectingMode();
  }, [enterReplaySelectingMode, isAuthLoaded, isSignedIn, isValidTimeframe, replayMode, restoreReplayContext]);

  useEffect(() => {
    if (replayMode !== 'selecting') {
      didAutoFetchSelectingRef.current = false;
      return;
    }

    if (!isAuthLoaded || !isSignedIn) return;
    if (!autoFetchSelectingMode) return;

    const hasCandles = (candlesLength || 0) > 0;
    if (hasCandles) return;
    if (isFetchingData || isInitialLoading || isRestoringSession) return;
    if (didAutoFetchSelectingRef.current) return;

    console.log('[useReplayAuthGate] Auto-fetching for selecting mode...');
    didAutoFetchSelectingRef.current = true;
    const ctx = replayAuthService.getPendingReplayContext() || undefined;
    Promise.resolve(autoFetchSelectingMode(ctx)).catch(err => {
      console.warn('[useReplayAuthGate] Auto-fetch failed for selecting mode:', err);
      didAutoFetchSelectingRef.current = false;
    });
  }, [
    replayMode,
    isAuthLoaded,
    isSignedIn,
    autoFetchSelectingMode,
    candlesLength,
    isFetchingData,
    isInitialLoading,
    isRestoringSession,
  ]);

  return { ensureSignedInForReplay, isAuthLoaded, isSignedIn, isPendingReplayRedirect };
}
